<template>
  <div class="tw-flex items-start justify-center min-h-screen bg-gray-50 py-10 px-4">
    <div class="w-full max-w-lg">
      <button
        @click="$router.go(-1)"
        class="tw-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-5 bg-transparent border-none cursor-pointer p-0"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
        </svg>
        Back
      </button>

      <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-8">

        <!-- Header -->
        <div class="mb-6">
          <h1 class="text-xl font-bold text-gray-900 m-0 mb-1">
            {{ isTrialMode ? 'Claim your free trial' : 'Subscribe to Transition Reports' }}
          </h1>
          <p class="text-sm text-gray-500 m-0">
            {{ isTrialMode
              ? 'Fill in your billing details to start your free trial. No payment required upfront.'
              : 'Fill in your billing details to proceed to secure payment.' }}
          </p>
        </div>

        <!-- Error banner -->
        <div v-if="errorMessage" class="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {{ errorMessage }}
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-4">

          <!-- Personal details -->
          <fieldset class="space-y-3">
            <legend class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Your details</legend>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">First name <span class="text-red-500">*</span></label>
                <input
                  id="first_name"
                  v-model="form.first_name"
                  type="text"
                  required
                  maxlength="100"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">Last name <span class="text-red-500">*</span></label>
                <input
                  id="last_name"
                  v-model="form.last_name"
                  type="text"
                  required
                  maxlength="100"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="email"
                :value="form.email"
                type="email"
                readonly
                class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
              />
              <p class="mt-1 text-xs text-gray-400">Your email address cannot be changed.</p>
            </div>
          </fieldset>

          <!-- Organisation details -->
          <fieldset class="space-y-3">
            <legend class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Organisation</legend>

            <div>
              <label for="company_name" class="block text-sm font-medium text-gray-700 mb-1">Company name <span class="text-red-500">*</span></label>
              <input
                id="company_name"
                v-model="form.company_name"
                type="text"
                required
                maxlength="255"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>

            <!-- Both trial and subscription mode use structured billing address -->
            <div>
              <label for="address_line1" class="block text-sm font-medium text-gray-700 mb-1">Street address <span class="text-red-500">*</span></label>
              <input
                id="address_line1"
                v-model="form.address_line1"
                type="text"
                required
                maxlength="500"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label for="address_city" class="block text-sm font-medium text-gray-700 mb-1">City <span class="text-red-500">*</span></label>
                <input
                  id="address_city"
                  v-model="form.address_city"
                  type="text"
                  required
                  maxlength="100"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label for="address_postal_code" class="block text-sm font-medium text-gray-700 mb-1">Postal code <span class="text-red-500">*</span></label>
                <input
                  id="address_postal_code"
                  v-model="form.address_postal_code"
                  type="text"
                  required
                  maxlength="20"
                  class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label for="address_country" class="block text-sm font-medium text-gray-700 mb-1">Country <span class="text-red-500">*</span></label>
              <select
                id="address_country"
                v-model="form.address_country"
                required
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="" disabled>Select a country</option>
                <option v-for="(name, code) in countries" :key="code" :value="code">{{ name }}</option>
              </select>
            </div>

            <div>
              <label for="vat_number" class="block text-sm font-medium text-gray-700 mb-1">VAT number</label>
              <div class="tw-flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary">
                <span
                  v-if="vatCountryPrefix"
                  class="tw-flex items-center px-3 text-sm font-semibold text-gray-500 bg-gray-50 border-r border-gray-300 shrink-0 select-none"
                >
                  {{ vatCountryPrefix }}
                </span>
                <input
                  id="vat_number"
                  v-model="form.vat_number"
                  type="text"
                  maxlength="50"
                  required
                  class="flex-1 px-3 py-2 text-sm focus:outline-none bg-transparent"
                />
              </div>
            </div>
          </fieldset>

          <!-- Submit -->
          <div class="pt-2">
            <button
              type="submit"
              :disabled="isSubmitting || !isFormValid"
              class="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting">Redirecting…</span>
              <span v-else>{{ isTrialMode ? 'Continue to free trial' : 'Continue to payment' }}</span>
            </button>
            <p v-if="isTrialMode" class="mt-2 text-center text-xs text-gray-400">
              No credit card required. Your trial will auto-convert to a paid subscription when it ends.
            </p>
            <p class="mt-3 text-center text-xs text-gray-400">
              Running into an issue?
              <a href="/about#contact" class="text-primary hover:underline">Contact us</a>
            </p>
          </div>

        </form>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { getCheckoutData, claimTrial } from '@/api/transitionReportsTrial'
  import { subscribe } from '@/api/transitionReportsSubscription'
  import { COUNTRIES } from '@/constants/countries'

  export default {
    name: 'TransitionReportCheckout',

    data () {
      return {
        form: {
          first_name: '',
          last_name: '',
          email: '',
          company_name: '',
          address_line1: '',
          address_city: '',
          address_postal_code: '',
          address_country: '',
          vat_number: '',
        },
        vatJurisdiction: '',   // ISO code from actor.jurisdiction, used as primary VAT prefix
        isSubmitting: false,
        errorMessage: null,
      }
    },

    computed: {
      countries () {
        return COUNTRIES
      },
      isTrialMode () {
        return this.$store.getters.isTransitionReportTrialAvailable
      },
      // Jurisdiction (actor-level) takes precedence; fallback to the selected billing country.
      vatCountryPrefix (): string {
        return this.vatJurisdiction || this.form.address_country?.toUpperCase() || ''
      },
      // Full VAT sent to the API: prefix + number, unless the user already typed the prefix.
      resolvedVatNumber (): string {
        const raw = this.form.vat_number.trim()
        const prefix = this.vatCountryPrefix
        if (!prefix || !raw || raw.toUpperCase().startsWith(prefix)) return raw
        return prefix + raw
      },
      isFormValid () {
        return (
          this.form.first_name.trim().length > 0 &&
          this.form.last_name.trim().length > 0 &&
          this.form.company_name.trim().length > 0 &&
          this.form.address_line1.trim().length > 0 &&
          this.form.address_city.trim().length > 0 &&
          this.form.address_postal_code.trim().length > 0 &&
          this.form.address_country.trim().length === 2 &&
          this.form.vat_number.trim().length > 0
        )
      },
    },

    async mounted () {
      // Pre-fill from query params (fallback from subscribeFromTrial)
      const q = this.$route.query
      if (q.first_name) this.form.first_name = q.first_name
      if (q.last_name) this.form.last_name = q.last_name
      if (q.company_name) this.form.company_name = q.company_name
      if (q.address_line1) this.form.address_line1 = q.address_line1
      if (q.address_city) this.form.address_city = q.address_city
      if (q.address_postal_code) this.form.address_postal_code = q.address_postal_code
      if (q.address_country) this.form.address_country = q.address_country
      if (q.vat_number) this.form.vat_number = q.vat_number

      // Load prefill data from API
      try {
        const data = await getCheckoutData()
        if (!this.form.first_name) this.form.first_name = data.user.first_name || ''
        if (!this.form.last_name) this.form.last_name = data.user.last_name || ''
        this.form.email = data.user.email || ''
        if (!this.form.company_name) this.form.company_name = data.organisation.name || ''
        if (!this.form.address_line1) this.form.address_line1 = data.organisation.address_line1 || ''
        if (!this.form.address_city) this.form.address_city = data.organisation.address_city || ''
        if (!this.form.address_postal_code) this.form.address_postal_code = data.organisation.address_postal_code || ''
        if (!this.form.address_country) this.form.address_country = this.resolveCountryCode(data.organisation.address_country)
        if (!this.form.vat_number) this.form.vat_number = data.organisation.vat_number || ''

        // Determine VAT prefix from jurisdiction, falling back to address country
        this.vatJurisdiction = data.organisation.jurisdiction?.trim().toUpperCase() || ''

        // Strip prefix from pre-filled VAT so the input only shows the numeric part
        const prefix = this.vatCountryPrefix
        if (prefix && this.form.vat_number.toUpperCase().startsWith(prefix)) {
          this.form.vat_number = this.form.vat_number.slice(prefix.length)
        }
      } catch (e) {
        this.errorMessage = 'Failed to load checkout data. Please refresh and try again.'
      }
    },

    methods: {
      // Actors store country as either an ISO code ("BE") or a full name ("Belgium").
      // Normalise to an uppercase ISO code so it matches the <select> options.
      resolveCountryCode (value: string | null | undefined): string {
        if (!value) return ''
        const upper = value.trim().toUpperCase()
        // Already a valid 2-letter ISO code
        if (this.countries[upper]) return upper
        // Full name — reverse lookup
        const lower = value.trim().toLowerCase()
        const entry = Object.entries(this.countries).find(([, name]) => (name as string).toLowerCase() === lower)
        return entry ? entry[0] : ''
      },

      async handleSubmit () {
        if (!this.isFormValid || this.isSubmitting) return

        this.isSubmitting = true
        this.errorMessage = null

        if (this.isTrialMode) {
          await this.handleTrialSubmit()
        } else {
          await this.handleSubscribeSubmit()
        }
      },

      async handleTrialSubmit () {
        try {
          const result = await claimTrial({
            first_name: this.form.first_name,
            last_name: this.form.last_name,
            company_name: this.form.company_name,
            address_line1: this.form.address_line1,
            address_city: this.form.address_city,
            address_postal_code: this.form.address_postal_code,
            address_country: this.form.address_country.toUpperCase(),
            vat_number: this.resolvedVatNumber || null,
          })

          window.location.href = result.checkout_url
        } catch (err) {
          if (err?.response?.status === 403) {
            this.errorMessage = 'You are not eligible for the free trial. Please contact us to get access to Transition Reports.'
          } else {
            this.errorMessage = err?.response?.data?.error
              || 'Something went wrong. Please try again or contact support.'
          }
          this.isSubmitting = false
        }
      },

      async handleSubscribeSubmit () {
        try {
          const result = await subscribe({
            first_name: this.form.first_name,
            last_name: this.form.last_name,
            company_name: this.form.company_name,
            address_line1: this.form.address_line1,
            address_city: this.form.address_city,
            address_postal_code: this.form.address_postal_code,
            address_country: this.form.address_country.toUpperCase(),
            vat_number: this.resolvedVatNumber || null,
          })

          window.location.href = result.checkout_url
        } catch (err) {
          this.errorMessage = err?.response?.data?.error
            || 'Something went wrong. Please try again or contact support.'
          this.isSubmitting = false
        }
      },
    },
  }
</script>
